import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  Modal,
  Button
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  ReceiptLong as ReceiptLongIcon,
  Download as DownloadIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';

const TablaTransporteValores = ({ datos, idNuevo, server, onRecarga, user }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('fechaEnvio');
  const [order, setOrder] = useState('desc');
  const [openModalPdf, setOpenModalPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfNombre, setPdfNombre] = useState('');

  useEffect(() => {
    if (!openModalPdf && pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl('');
    }
  }, [openModalPdf]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    if (!datos || datos.length === 0) return [];
    return [...datos].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];
      if (typeof aVal === 'number' && typeof bVal === 'number')
        return order === 'asc' ? aVal - bVal : bVal - aVal;
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [datos, order, orderBy]);

  const paginatedData = useMemo(
    () => sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedData, page, rowsPerPage]
  );

  const formatNumber = (value) => {
    if (value === null || value === undefined) return 'L 0.00';
    return new Intl.NumberFormat('es-HN', { style: 'currency', currency: 'HNL' }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (date.getFullYear() <= 1900) return '-';
    return date.toLocaleDateString('es-HN', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const columnas = [
    { id: 'acciones', label: 'Acciones', minWidth: 80, align: 'center' },
    { id: 'codigoInterno', label: 'Código PTV', minWidth: 160 },
    { id: 'codigoCierre', label: 'Cód. Cierre', minWidth: 120 },
    { id: 'trazabilidad', label: 'Trazabilidad', minWidth: 150 },
    { id: 'empresaServicio', label: 'Empresa Servicio', minWidth: 150 },
    { id: 'fechaEnvio', label: 'Fecha Envío', minWidth: 120 },
    { id: 'fechaDeposito', label: 'Fecha Depósito', minWidth: 130 },
    { id: 'fechaArqueo', label: 'Fecha Arqueo', minWidth: 120 },
    { id: 'totalEfectivoEnvio', label: 'Total Efectivo', minWidth: 130, align: 'right' },
    { id: 'observacion', label: 'Observación', minWidth: 180 },
    { id: 'ingresadoPor', label: 'Ingresado Por', minWidth: 130 },
    { id: 'idPartidaEnvio', label: 'Partida Envío', minWidth: 110, align: 'center' },
    { id: 'idPartidaDeposito', label: 'Partida Depósito', minWidth: 120, align: 'center' },
  ];

  const verPartidaContable = async (idPartida) => {
    if (!idPartida) return;
    try {
      Swal.fire({ title: 'Cargando partida...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      const response = await fetch(`${server}/InformesContablesLinux/asiento/${idPartida}/ReportAsiento`, {
        method: 'GET',
        headers: { 'Accept': 'application/pdf' }
      });
      if (!response.ok) throw new Error(`Error al generar el PDF: ${response.status}`);
      const blob = await response.blob();
      const urlBlob = window.URL.createObjectURL(blob);
      const nombreArchivo = `Asiento_${idPartida}_${new Date().toISOString().split('T')[0]}.pdf`;
      setPdfUrl(urlBlob);
      setPdfNombre(nombreArchivo);
      setOpenModalPdf(true);
      Swal.close();
    } catch (err) {
      console.error('Error al ver partida contable:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo generar el PDF de la partida' });
    }
  };

  const handleCloseModalPdf = () => setOpenModalPdf(false);

  const handleDownloadPdf = () => {
    if (pdfUrl && pdfNombre) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = pdfNombre;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDepositar = async (row) => {
    const { value: fechaDeposito } = await Swal.fire({
      title: 'Registrar Depósito',
      html: `<strong>${row.codigoInterno}</strong>${row.codigoCierre ? ` &mdash; Cierre: <strong>${row.codigoCierre}</strong>` : ''}<br/>Seleccione la fecha de depósito:`,
      input: 'date',
      inputValue: new Date().toISOString().split('T')[0],
      inputLabel: 'Fecha de depósito',
      showCancelButton: true,
      confirmButtonText: 'Depositar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1565c0',
      inputValidator: (val) => !val ? 'Debe seleccionar una fecha' : null
    });

    if (!fechaDeposito) return;

    Swal.fire({
      title: 'Registrando depósito...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${server}/TransporteValores/PTV-Deposito`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            idtransportevalores: row.idTransporteValores,
            fechaDeposito: new Date(fechaDeposito).toISOString(),
            modificadoPor: user?.codigoUsuario || null
          })
        }
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || err.error || 'Error al registrar el depósito');
      }

      Swal.fire({ icon: 'success', title: 'Depósito Registrado', timer: 2000, showConfirmButton: false });
      if (onRecarga) onRecarga();
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: 'error', title: 'Error', text: err.message });
    }
  };

  if (!datos || datos.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', my: 6, py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          No se encontraron registros de transporte de valores
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Usa el botón "Nuevo Envío PTV" para registrar un envío
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columnas.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align || 'left'}
                  style={{ minWidth: col.minWidth, fontWeight: 'bold' }}
                >
                  {col.id === 'acciones' ? col.label : (
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? order : 'asc'}
                      onClick={() => handleRequestSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, index) => {
              const esNuevo = idNuevo && row.id === idNuevo;
              return (
                <TableRow
                  hover
                  key={row.id || index}
                  sx={esNuevo ? { backgroundColor: '#e8f5e9', fontWeight: 'bold' } : {}}
                >
                  <TableCell align="center">
                    <Tooltip title={row.fechaDeposito && new Date(row.fechaDeposito).getFullYear() > 1900 ? 'Ya depositado' : 'Registrar Depósito'}>
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleDepositar(row)}
                          disabled={!!(row.fechaDeposito && new Date(row.fechaDeposito).getFullYear() > 1900)}
                        >
                          <AccountBalanceIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {esNuevo ? (
                      <Chip label={row.codigoInterno || '-'} color="success" size="small" />
                    ) : (
                      row.codigoInterno || '-'
                    )}
                  </TableCell>
                  <TableCell>{row.codigoCierre || '-'}</TableCell>
                  <TableCell>{row.trazabilidad || '-'}</TableCell>
                  <TableCell>{row.empresaServicio || '-'}</TableCell>
                  <TableCell>{formatDate(row.fechaEnvio)}</TableCell>
                  <TableCell>
                    {row.fechaDeposito && new Date(row.fechaDeposito).getFullYear() > 1900
                      ? <Chip label={formatDate(row.fechaDeposito)} color="success" size="small" />
                      : <Chip label="Pendiente" color="warning" size="small" variant="outlined" />}
                  </TableCell>
                  <TableCell>{formatDate(row.fechaArqueo)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatNumber(row.totalEfectivoEnvio)}
                  </TableCell>
                  <TableCell>{row.observacion || '-'}</TableCell>
                  <TableCell>{(row.ingresadoPor || '').trim()}</TableCell>
                  <TableCell align="center">
                    {row.idPartidaEnvio ? (
                      <Tooltip title={`Ver Partida Envío #${row.idPartidaEnvio}`}>
                        <IconButton size="small" color="success" onClick={() => verPartidaContable(row.idPartidaEnvio)}>
                          <ReceiptLongIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {row.idPartidaDeposito ? (
                      <Tooltip title={`Ver Partida Depósito #${row.idPartidaDeposito}`}>
                        <IconButton size="small" color="info" onClick={() => verPartidaContable(row.idPartidaDeposito)}>
                          <ReceiptLongIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={sortedData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
      />

      {/* Modal para visualizar PDF de partida contable */}
      <Modal open={openModalPdf} onClose={handleCloseModalPdf}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: '90%', height: '90vh', bgcolor: 'white', boxShadow: 24, borderRadius: 2,
          display: 'flex', flexDirection: 'column', overflow: 'hidden'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #ddd', bgcolor: '#f5f5f5' }}>
            <Typography variant="h6">{pdfNombre}</Typography>
            <Box>
              <IconButton onClick={handleDownloadPdf} color="primary" title="Descargar PDF" sx={{ mr: 1 }}>
                <DownloadIcon />
              </IconButton>
              <IconButton onClick={handleCloseModalPdf} color="error" title="Cerrar">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ flex: 1, overflow: 'hidden', bgcolor: '#525659' }}>
            {pdfUrl && (
              <iframe src={pdfUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="Visor de PDF" />
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, borderTop: '1px solid #ddd', bgcolor: '#f5f5f5' }}>
            <Button variant="contained" color="error" onClick={handleCloseModalPdf} startIcon={<CloseIcon />}>
              Cerrar
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default TablaTransporteValores;
